import type { CourtWatchLink } from './courtWatchLinks';
import { dayKeyInZone } from '@/lib/dayKey';
import { courtsAtVenue, type EventVenue, type EventVenueContext } from './venues';

/**
 * Everything the Venues tab and the Overview venue summary derive from data that already
 * exists. Pure — no I/O, no React — so server pages, client components and tests share one
 * rule. Phase 4 of the public event page redesign.
 */

export const directionsHref = (v: { name: string; address: string | null }) =>
  `https://maps.google.com/?q=${encodeURIComponent([v.name, v.address].filter(Boolean).join(', '))}`;

export const telHref = (phone: string) => `tel:${phone.replace(/\s+/g, '')}`;

export type CourtsSummary =
  | { kind: 'none' }
  | { kind: 'range'; from: number; to: number }
  | { kind: 'list'; names: string[] };

const NUMBERED = /^court\s+(\d+)$/i;

/**
 * "Courts 1–4" from court names. Courts are names on the event's settings, not numbered rows,
 * so a range is claimed only when every name is "Court N" with distinct, contiguous numbers
 * (two or more). Anything else lists the names as the organiser typed them.
 */
export function summariseCourts(courts: { name: string }[]): CourtsSummary {
  if (courts.length === 0) return { kind: 'none' };
  const numbers: number[] = [];
  for (const c of courts) {
    const m = NUMBERED.exec(c.name.trim());
    if (!m) return { kind: 'list', names: courts.map((x) => x.name) };
    numbers.push(Number(m[1]));
  }
  const sorted = [...numbers].sort((a, b) => a - b);
  const contiguous = sorted.every((n, i) => i === 0 || n === sorted[i - 1]! + 1);
  if (courts.length >= 2 && contiguous)
    return { kind: 'range', from: sorted[0]!, to: sorted[sorted.length - 1]! };
  return { kind: 'list', names: courts.map((x) => x.name) };
}

export type RouteStop = {
  venue: EventVenue;
  courtCount: number;
  /** Minutes to the next stop; always null on the last stop. */
  travelMinToNext: number | null;
};

/** The listed venues in order, with their court counts and the travel time to the next one. */
export function routeStops(ctx: EventVenueContext): RouteStop[] {
  const venues = ctx.venues.filter((x) => !x.isPrimary);
  return venues.map((venue, i) => ({
    venue,
    courtCount: courtsAtVenue(ctx, venue.id).length,
    travelMinToNext: i < venues.length - 1 ? (venue.travelMinToNext ?? null) : null,
  }));
}

export type TodayHere = { count: number; firstAt: string };

type ScheduleSlice = {
  selectedDay: string;
  timezone: string;
  courts: { id: string; venueId: string | null }[];
  matches: { courtId: string; scheduledAt: string }[];
};

/**
 * Per venue: how many matches are scheduled today and when the first starts. `schedule` is
 * `getCachedPublicSchedule(eventId)` with no day, whose `selectedDay` is today only when today
 * has matches — so a selected day that is not today in the event's zone yields nothing.
 */
export function todayByVenue(schedule: ScheduleSlice, nowIso: string): Map<string, TodayHere> {
  const out = new Map<string, TodayHere>();
  if (schedule.selectedDay !== dayKeyInZone(nowIso, schedule.timezone)) return out;
  const venueOfCourt = new Map(schedule.courts.map((c) => [c.id, c.venueId]));
  for (const m of schedule.matches) {
    const venueId = venueOfCourt.get(m.courtId);
    if (!venueId) continue;
    const cur = out.get(venueId);
    if (!cur) out.set(venueId, { count: 1, firstAt: m.scheduledAt });
    else {
      cur.count += 1;
      if (Date.parse(m.scheduledAt) < Date.parse(cur.firstAt)) cur.firstAt = m.scheduledAt;
    }
  }
  return out;
}

/** The Schedule deep link: the day, and the venue filter only when the schedule offers one (more than one venue). */
export function scheduleHref(
  base: string,
  venueId: string,
  multiVenue: boolean,
  day: string,
): string {
  const q = new URLSearchParams({ day });
  if (multiVenue) q.set('venue', venueId);
  return `${base}/schedule?${q.toString()}`;
}

/** "9:00 am" in the display zone. `(iso, locale, timeZone?)` so it drops into `<LocalDateTime format>`. */
export function formatTimeOfDay(iso: string, locale: string, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone }).format(
    d,
  );
}

export type OverviewVenueSummary = {
  first: EventVenue;
  more:
    | { kind: 'none' }
    | { kind: 'one'; name: string; minutes: number | null }
    | { kind: 'many'; count: number };
};

/** The Overview's short venue block: the first venue in full, the rest in one line. */
export function overviewVenueSummary(venues: EventVenue[]): OverviewVenueSummary | null {
  const [first, ...rest] = venues;
  if (!first) return null;
  if (rest.length === 0) return { first, more: { kind: 'none' } };
  if (rest.length === 1)
    return {
      first,
      more: { kind: 'one', name: rest[0]!.name, minutes: first.travelMinToNext ?? null },
    };
  return { first, more: { kind: 'many', count: rest.length } };
}

/** Serializable server-loaded details for the public venue explorer. */
export type VenuePageItem = RouteStop & {
  courts: CourtsSummary;
  courtWatchLinks?: (CourtWatchLink & { courtName: string })[];
  today: TodayHere | null;
  scheduleHref: string | null;
};

/** Fixed-origin map search of a usable postal address, never an organiser-provided embed URL. */
export function venueMapEmbedHref(venue: Pick<EventVenue, 'name' | 'address'>): string | null {
  const raw = venue.address;
  if (!raw || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(raw)) return null;
  const address = raw.replace(/\s+/g, ' ').trim();
  if (!address || /^(?:[a-z][a-z\d+.-]*:|\/\/|www\.)/i.test(address)) return null;
  return `https://maps.google.com/?q=${encodeURIComponent(address)}&output=embed`;
}
